import { useLayoutEffect, useState, type ReactNode } from 'react';
import { useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { AccessibleTenant } from '@atnt/shared';
// direct module import (not the feature barrel): the alarm feature's pages import the shell
import { AlarmCenter } from '@/features/alarms/AlarmCenter';
import { NoAccess } from './ComingSoon';
import { canAccessPath, resolveTab } from './nav';
import { PageHeaderBar, PageHeaderProvider } from './PageHeader';
import { Sidebar } from './Sidebar';
import { bindSidebarUser } from './sidebar-state';
import { bindTabsUser, syncRouteTab, useTabsStore } from './tabs';
import { TopTabStrip } from './TopTabStrip';
import { useMe } from './use-me';

/** Bind the per-user shell stores (tabs, sidebar groups). Idempotent; also called from the layouts' beforeLoad. */
export function bindShellUser(userId: string) {
  bindTabsUser(userId);
  bindSidebarUser(userId);
}

/**
 * Application shell (01-design-system §6.1): sidebar 240 (64 collapsed) · tab strip h32 · page header h44 · page.
 * Pages render inside `main` and lay themselves out with <PageLayout> / <ContentPanel>; the header is filled with
 * <PageHeader>. Pass `sidebar` to replace the tree (e.g. the section viewer's flat list, 590:16362).
 */
export function AppShell({ tenant, sidebar, children }: { tenant?: AccessibleTenant; sidebar?: ReactNode; children: ReactNode }) {
  const { t } = useTranslation('shell');
  const me = useMe();
  useState(() => bindShellUser(me.user.id));
  const location = useLocation();
  const allowed = canAccessPath(location.pathname, me, tenant);

  // every navigation focuses (or opens) the tab of the current page — before paint, so the strip never lags.
  // Pages the user may not see get no tab.
  useLayoutEffect(() => {
    syncRouteTab(location.href, allowed ? location.pathname : '');
  }, [location.href, location.pathname, allowed]);

  const activeTab = useTabsStore((s) => s.tabs.find((x) => x.key === s.activeKey));
  const spec = resolveTab(location.pathname);
  const fallbackTitle = activeTab?.title || (spec ? t(spec.titleKey) : undefined);

  return (
    <PageHeaderProvider>
      <div className="flex h-full w-full overflow-hidden bg-app">
        {sidebar ?? <Sidebar tenant={tenant} />}
        <div className="flex min-w-0 flex-1 flex-col">
          <TopTabStrip />
          <PageHeaderBar fallbackTitle={fallbackTitle} trailing={tenant ? <AlarmCenter tenant={tenant} /> : null} />
          <main className="relative flex min-h-0 flex-1 flex-col">{allowed ? children : <NoAccess />}</main>
        </div>
      </div>
    </PageHeaderProvider>
  );
}
